const getEnvVar = (key, defaultValue = null) => {
  const fullKey = `REACT_APP_${key}`;
  const value = process.env[fullKey];

  if (!value && defaultValue === null) {
    // Only throw error in production, warn in development
    if (process.env.NODE_ENV === 'production') {
      console.error(`Missing required environment variable: ${fullKey}`);
    } else {
      console.warn(`Missing environment variable: ${fullKey}, using default`);
    }
  }

  return value || defaultValue;
};

// Application Configuration
const config = {
  // API Configuration
  API: {
    BASE_URL: process.env.REACT_APP_API_BASE_URL,

    // API timeout in milliseconds
    TIMEOUT: process.env.REACT_APP_API_TIMEOUT,

    Websocket_tenant_Base_URL: process.env.REACT_APP_WS_TENANT_URL,
    Websocket_conversation_Base_URL: process.env.REACT_APP_WS_CONVERSATION_URL,
    Websocket_user_Base_URL: process.env.REACT_APP_WS_USER_URL,
    /** Agent-to-agent internal chat WebSocket base URL (separate from tenant/conversation/user streams). */
    Websocket_internal_chat_Base_URL: process.env.REACT_APP_WS_INTERNAL_CHAT_URL,

    // API Endpoints
    ENDPOINTS: {
      // Authentication endpoints
      AUTH: {
        LOGIN: '/api/auth/login/',
        SIGNUP: '/api/auth/register/',
        FORGOT_PASSWORD: '/api/auth/forgot-password/',
        RESET_PASSWORD: '/api/auth/reset-password/',
        CHANGE_PASSWORD: '/api/auth/update-password/',
        APPROVE_USER: '/api/auth/approve-user/',
        LOGOUT: '/api/auth/logout/',
        VERIFY_TOKEN: '/api/auth/token/verify/',
        REFRESH_TOKEN: '/api/auth/token/refresh/',
        TENANT_CONFIG: '/api/tenant/config/',
        INACTIVITY_ALERTS: '/api/tenant/config/inactivity-alerts/',
        INACTIVITY_ALERTS_ACTIVATE: '/api/tenant/config/inactivity-alerts/activate/',
        INACTIVITY_ALERTS_DEACTIVATE: '/api/tenant/config/inactivity-alerts/deactivate/',
        NO_REPLY_RESOLVE: '/api/tenant/config/no-reply-resolve/',
        NO_REPLY_RESOLVE_ACTIVATE: '/api/tenant/config/no-reply-resolve/activate/',
        NO_REPLY_RESOLVE_DEACTIVATE: '/api/tenant/config/no-reply-resolve/deactivate/',
        FEEDBACK_REQUEST: '/api/tenant/config/feedback-request/',
        FEEDBACK_REQUEST_ACTIVATE: '/api/tenant/config/feedback-request/activate/',
        FEEDBACK_REQUEST_DEACTIVATE: '/api/tenant/config/feedback-request/deactivate/',
        BOT_AUTO_RESOLVE: '/api/tenant/config/bot-auto-resolve/',
        BOT_AUTO_RESOLVE_ACTIVATE: '/api/tenant/config/bot-auto-resolve/activate/',
        BOT_AUTO_RESOLVE_DEACTIVATE: '/api/tenant/config/bot-auto-resolve/deactivate/',
        ALERTS_ALARM: '/api/tenant/config/alerts-alarm/',
        ALERTS_ALARM_ACTIVATE: '/api/tenant/config/alerts-alarm/activate/',
        ALERTS_ALARM_DEACTIVATE: '/api/tenant/config/alerts-alarm/deactivate/',
        AI_AGENT_ENUM: '/api/auth/ai-agent-enum/'
      },

      // User management endpoints
      USER: {
        PROFILE: '/user/profile',
        UPDATE_PROFILE: '/user/update',
        UPDATE_USER: '/api/users/update-user',
        UPDATE_USER_APPROVAL: '/api/users/update-user-approval',
        GET_USERS: '/users',
        GET_USERS_LIST: '/api/users/get-users-list/',
        GET_AGENT_FILTER_LIST: '/api/users/agent-filter-list/',
        DELETE_USER: '/user/delete',
        SOUND_SETTINGS: '/api/users/update-sound-settings/',
        PROFILE_PIC: '/api/users/me/profile-pic/',
        AGENT_AVAILABILITY_STATUS: '/api/tenant/agent/availability-status/'
      },

      // Chat endpoints
      CHAT: {
        GET_ALL_CHATS: '/api/chats/All',
        GET_INBOX_CHATS: '/api/chats/Inbox/',
        GET_MY_CHATS: '/api/chats/My/',
        GET_ALERTS_CHATS: '/api/chats/Alerts',
        GET_PIN_CHATS: '/api/chats/pinned',
        GET_USER_CHATS: '/api/chats',
        SEND_MESSAGE: '/api/chats',
        TRANSFER_CHAT: '/api/chats',
        RESOLVE_CHAT: '/api/chats',
        LEAVE_CHAT: '/api/chats',
        INVITE_USER: '/api/chats/invite',
        GET_CONVERSATIONS: '/conversations/',
        GET_CONVERSATION: '/conversations',
        GET_MESSAGES: '/api/chats/conversations',
        MARK_READ: '/chat/mark-read',
        GET_CONTACTS: '/contacts/',
        CREATE_CONTACT: '/contacts/',
        TRANSFER_CHAT: '/api/chats/conversations',
        CREATE_FAQ: '/api/knowledgebase/faq/create/',
        GET_CONVERSATIONS_BY_CONTACT: '/api/chats/conversations/by-contact/',
        CHAT_ACCEPTANCE_SLA_CONFIG: '/api/chats/chat-acceptance-sla/configuration/',
        CHAT_ACCEPTANCE_SLA_ACTIVATE: '/api/chats/chat-acceptance-sla/activate/',
        CHAT_ACCEPTANCE_SLA_DEACTIVATE: '/api/chats/chat-acceptance-sla/deactivate/',
        CHAT_RESOLVE_SLA_CONFIG: '/api/chats/chat-resolve-sla/configuration/',
        CHAT_RESOLVE_SLA_ACTIVATE: '/api/chats/chat-resolve-sla/activate/',
        CHAT_RESOLVE_SLA_DEACTIVATE: '/api/chats/chat-resolve-sla/deactivate/'
      },

      // Support center endpoints
      SUPPORT: {
        GET_CENTERS: '/support-centers',
        CREATE_CENTER: '/support-center/create',
        UPDATE_CENTER: '/support-center/update',
        DELETE_CENTER: '/support-center/delete'
      },

      // Dashboard endpoints
      DASHBOARD: {
        GET_STATS: '/dashboard/stats',
        GET_ACTIVITY: '/dashboard/activity',
        GET_REPORTS: '/dashboard/reports',
        CONVERSATION_METRICS: '/api/reports/conversation/metrics',
        CONVERSATION_ASSIGNMENTS: '/api/reports/conversation/assignments/',
        AGENT_PERFORMANCE: '/api/reports/agent-performance/',
        AGENT_CUSTOMERS: '/api/reports/agent-performance/{agent_id}/customers/',
        DAILY_CUSTOMER_INTERACTIONS: '/api/reports/daily-customer-interactions'
      },

      // Reports endpoints
      REPORTS: {
        CONVERSATION_LIST: '/api/reports/conversation/list',
        CONVERSATION_ACTIONS: '/api/reports/conversation/actions/',
        AGENT_STATISTICS: '/api/reports/agent/statistics',
        BITRIX_ISSUES: '/api/reports/bitrix/issues/',
        BITRIX_AGENT_PERFORMANCE: '/api/reports/bitrix/agent-performance/',
        FAILED_RESPONSES: '/api/reports/failed-responses/',
        FAILED_RESPONSES_STATISTICS: '/api/reports/failed-responses/statistics/',
        FAILED_RESPONSES_ACTIVITY_LOGS: '/api/reports/failed-responses/{failed_response_id}/activity-logs/',
        FAILED_RESPONSES_EXPORT: '/api/reports/failed-responses/export/',
        CUSTOMER_SATISFACTION: '/api/reports/customer-satisfaction/',
        CUSTOMER_SATISFACTION_EXPORT: '/api/reports/customer-satisfaction/export/',
        RATED_CONVERSATIONS: '/api/reports/rated-conversations/',
        RATED_CONVERSATIONS_EXPORT: '/api/reports/rated-conversations/export/'
      },

      // WhatsApp Template endpoints
      TEMPLATES: {
        LIST: '/api/wa/templates/',
        FILTER: '/api/wa/templates/filtered/',
        CREATE: '/api/wa/templates/create/',
        SEND: '/api/wa/templates/send/',
        MEDIA_UPLOAD: '/api/wa/templates/media-upload/',
        SEND_MEDIA_UPLOAD: '/api/wa/templates/send-media-upload/',
      },

      // Quick Phrase endpoints
      QUICK_PHRASES: {
        LIST: '/api/chats/quick-phrases/',
        CREATE: '/api/chats/quick-phrases/create/',
        /** Base path; append `/${id}/` (detail), `/${id}/update/`, `/${id}/delete/`, `/${id}/activity-logs/`. */
        BASE: '/api/chats/quick-phrases',
      },

      // Tenant Bot Prompt endpoints
      BOT_PROMPTS: {
        /**
         * Base path. GET lists all prompts, POST creates.
         * Append `/${id}/` (detail/update/delete), `/${id}/activate/`, `/${id}/deactivate/`,
         * `/${id}/reset/`, `/${id}/audit-logs/`.
         */
        BASE: '/api/chatbot/bot-prompts',
      },

      // FAQ/Knowledge Base endpoints
      FAQ: {
        CREATE_FAQ: '/api/knowledgebase/faq/create/',
        PENDING_FAQ: '/api/knowledgebase/faq/pending/',
        REJECTED_FAQ: '/api/knowledgebase/faq/rejected/',
        APPROVED_FAQ: '/api/knowledgebase/faq/approved/',
        KNOWLEDGE_BASE_FAQ: '/api/knowledgebase/faq/added/',
        APPROVE_FAQ: '/api/knowledgebase/faq',
        REJECT_FAQ: '/api/knowledgebase/faq',
        EDIT_FAQ: '/api/knowledgebase/faq',
        BULK_ACCEPT_FAQ: '/api/knowledgebase/faq/bulk-accept/',
        BULK_REJECT_FAQ: '/api/knowledgebase/faq/bulk-reject/',
        DELETE_REJECTED_FAQ: '/api/knowledgebase/faq',
        BULK_DELETE_REJECTED_FAQ: '/api/knowledgebase/faq/bulk-delete-rejected/',
        ADD_TO_KNOWLEDGE_BASE: '/api/knowledgebase/faq/add-to-knowledgebase/',
        REMOVE_FROM_KNOWLEDGE_BASE: '/api/knowledgebase/faq/remove-from-knowledgebase/',
        FAQ_STATS: '/api/knowledgebase/faq/stats/',
        STATS_KNOWLEDGE_BASE: '/api/knowledgebase/faq/stats-knowledgebase/',
        REFRESH_KNOWLEDGE_BASE: '/api/knowledgebase/faq/refreach-knowledgebase/',
        NOTE_FAQ: '/api/knowledgebase/faq',
        EXPORT_KNOWLEDGE_BASE: '/api/knowledgebase/faq/export-knowledgebase/',
        IMPORT_FAQ: '/api/knowledgebase/faq/import-faq/',
        /** Base path; append `/${faqId}/activity-logs/` for activity logs. */
        FAQ_ACTIVITY_LOGS: '/api/knowledgebase/faq',
      },

      // External API endpoints
      EXTERNAL: {
        BITRIX_CREATE_TICKET: '/api/externalapi/tickets/bitrix/'
      },

      // Bitrix endpoints
      BITRIX: {
        CREATE_TICKET: '/api/bitrix/tickets/',
        CONTACT_SUGGEST: '/api/bitrix/contacts/suggest/',
        TICKETS_BY_CONVERSATION: '/api/bitrix/tickets/by-conversation/',
        TICKET_DETAILS: '/api/bitrix/ticket/',
        TICKET_TYPES: '/api/bitrix/ticket-types/',
        FIELDS: '/api/bitrix/fields/'
      },

      // Integration endpoints
      INTEGRATIONS: {
        GET: '/api/tenant/integrations/',
        UPDATE: '/api/tenant/integrations/',
        GOOGLE_MEET_AUTH: '/api/tenant/integrations/calling/GoogleMeet/'
      },

      // Meet/Call Logs endpoints
      MEET: {
        CALL_LOGS: '/api/meet/call-logs/',
        CALL_LOGS_EXPORT: '/api/meet/call-logs/export/'
      },

      // Telegram endpoints
      TELEGRAM: {
        GET_CONFIG: '/api/telegram/config/get/',
        SAVE_CONFIG: '/api/telegram/config/',
        DISABLE_CONFIG: '/api/telegram/config/disable/',
        ACTIVATE_CONFIG: '/api/telegram/config/activate/',
      },

      /**
       * Internal (team) chat — agent-to-agent; separate handlers from ENDPOINTS.CHAT.
       */
      INTERNAL_CHAT: {
        GET_LIST: '/api/chats/internal-chats/',
        GET_LIST_FAVORITE: '/api/chats/internal-chats/favorite',
        GET_LIST_GROUP: '/api/chats/internal-chats/group',
        CREATE_GROUP: '/api/chats/create-internal-group-chat/',
        CREATE_DIRECT: '/api/chats/create-internal-chat/',
      }
    }
  },

  // App Configuration
  APP: {
    NAME: getEnvVar('APP_NAME', 'Supbotive-Frontend'),
    VERSION: getEnvVar('APP_VERSION', '1.0.0'),
    ENVIRONMENT: process.env.NODE_ENV || 'development'
  },

  // Feature Flags
  FEATURES: {
    ENABLE_ANALYTICS: getEnvVar('ENABLE_ANALYTICS', 'false') === 'true',
    ENABLE_ERROR_TRACKING: getEnvVar('ENABLE_ERROR_TRACKING', 'false') === 'true',
    ENABLE_DEBUG_MODE: getEnvVar('ENABLE_DEBUG_MODE', 'false') === 'true'
  },

  // UI Configuration
  UI: {
    THEME: {
      PRIMARY_COLOR: '#3b82f6', // blue-600
      SECONDARY_COLOR: '#6b7280', // gray-500
      SUCCESS_COLOR: '#10b981', // emerald-500
      ERROR_COLOR: '#ef4444', // red-500
      WARNING_COLOR: '#f59e0b' // amber-500
    },
    PAGINATION: {
      DEFAULT_PAGE_SIZE: 10,
      MAX_PAGE_SIZE: 100
    }
  }
};

// Helper function to build full API URLs
export const buildApiUrl = (endpoint) => {
  return `${config.API.BASE_URL}${endpoint}`;
};

// Helper function to get specific endpoint
export const getEndpoint = (category, name) => {
  return config.API.ENDPOINTS[category]?.[name] || '';
};

// Helper function to get full URL for specific endpoint
export const getApiUrl = (category, name) => {
  const endpoint = getEndpoint(category, name);
  return endpoint ? buildApiUrl(endpoint) : '';
};

// Helper function to build chat URLs with user ID
export const buildChatUrl = (endpoint, userId, action = '') => {
  const baseEndpoint = getEndpoint('CHAT', endpoint);
  if (action) {
    return `${config.API.BASE_URL}${baseEndpoint}/${userId}/${action}`;
  }
  return `${config.API.BASE_URL}${baseEndpoint}/${userId}`;
};

// Validate critical configuration on import
if (process.env.NODE_ENV === 'production') {
  if (!config.API.BASE_URL) {
    console.error('❌ API_BASE_URL is required in production');
  }
}

// Export the main config object
export default config; 